// Bucket A telemetry event taxonomy.
// Anonymous, no content. Allowlist enforced via Zod at track() time.
// See: vault/.../research/data-collection-engine/_index.md §4.2

import { z } from 'zod/v4';

// Object-Action grammar, snake_case, past tense.
// Adding events: add to EventName + EventSchema, never log unlisted properties.
export const EVENT_NAMES = [
  'app_opened',
  'page_viewed',
  'settings_changed',
  'mood_logged',
  'habit_toggled',
  'cycle_closed',
  'report_generated',
  'report_engaged',
  'feature_discovered',
  'error_shown',
  'onboarding_step_viewed',
  'onboarding_completed',
  'consent_decided',
  'tour_started',
  'tour_step_viewed',
  'tour_section_skipped',
  'tour_completed',
  'lcp_measured',
] as const;

export type EventName = (typeof EVENT_NAMES)[number];

// Per-event property schemas. Strict; unknown properties are stripped.
const EventSchemas = {
  app_opened: z.object({
    locale: z.string().optional(),
    theme: z.enum(['dark', 'light']).optional(),
    is_pwa: z.boolean().optional(),
  }),
  page_viewed: z.object({
    route: z.string(),
  }),
  settings_changed: z.object({
    setting_id: z.string(),
  }),
  mood_logged: z.object({
    has_note: z.boolean(),
  }),
  habit_toggled: z.object({
    action: z.enum(['done', 'undone']),
  }),
  cycle_closed: z.object({
    duration_days: z.number().int().nonnegative(),
  }),
  report_generated: z.object({
    type: z.enum(['weekly', 'monthly', 'cycle', 'pulse']),
    model: z.string(),
    latency_ms: z.number().int().nonnegative().optional(),
  }),
  report_engaged: z.object({
    action: z.enum(['opened', 'dismissed', 'regenerated', 'shared', 'helpful']),
    dwell_sec: z.number().int().nonnegative().optional(),
  }),
  feature_discovered: z.object({
    feature_id: z.string(),
  }),
  error_shown: z.object({
    code: z.string(),
    route: z.string().optional(),
  }),
  // First-run funnel (owner brief, 2026-08-21). The sequence is slides ->
  // door -> consent -> tour, and until these three events existed the only
  // measurable part of it was the tour. A drop-off inside the slides, or a
  // user who declined the optional buckets, was invisible.
  //
  // A KNOWN AND DELIBERATE BLIND SPOT: telemetry cannot start before Bucket A
  // is granted, and Bucket A is asked AFTER the slides. So the slide events
  // land only for users who already consented on an earlier session (a replay,
  // a second device). Consent-first is the ruling; the alternative is
  // measuring people who have not agreed to be measured.
  onboarding_step_viewed: z.object({
    step: z.string(),
    index: z.number().int().nonnegative(),
  }),
  onboarding_completed: z.object({
    // A user who pressed Skip finished the sequence without reading it. Same
    // completion in the database, a very different signal here.
    skipped: z.boolean(),
  }),
  consent_decided: z.object({
    bucket: z.enum(['A', 'B', 'C']),
    granted: z.boolean(),
  }),
  tour_started: z.object({
    tour_id: z.string(),
    source: z.enum(['auto', 'replay', 'manual']),
  }),
  tour_step_viewed: z.object({
    tour_id: z.string(),
    step_index: z.number().int().nonnegative(),
    step_id: z.string(),
  }),
  tour_section_skipped: z.object({
    tour_id: z.string(),
    phase_id: z.string(),
    at_step: z.number().int().nonnegative(),
  }),
  tour_completed: z.object({
    tour_id: z.string(),
    duration_ms: z.number().int().nonnegative(),
    completed_steps: z.number().int().nonnegative(),
  }),
  // The Largest Contentful Paint field assertion (Phase 09, UI-SPEC S8.4). It exists to
  // make D-39's analysis FALSIFIABLE rather than theoretical: the claim is that a
  // full-viewport fixed scene is disqualified from LCP eligibility, so the LCP element is
  // the display headline text. If `url` ever comes back as a scene image URL, that
  // exclusion did not apply and the analysis is wrong for the shipped markup.
  //
  // THE PAYLOAD IS DELIBERATELY THIN. `element` is a TAG NAME and never the element's text
  // or markup: the headline is localised product copy, and the assertion needs the tag, the
  // resource url and the pixel area, none of which carry user or product content.
  lcp_measured: z.object({
    // Tag name only, e.g. "H1" or "IMG". Never text content, never innerHTML.
    element: z.string(),
    // Empty string for a text LCP, which is the outcome this assertion predicts.
    url: z.string(),
    size: z.number().int().nonnegative(),
    route: z.string(),
  }),
} as const;

type EventPropsMap = {
  [K in EventName]: z.infer<(typeof EventSchemas)[K]>;
};

export function validateEvent<E extends EventName>(
  name: E,
  props: EventPropsMap[E],
): EventPropsMap[E] | null {
  const schema = EventSchemas[name];
  const parsed = schema.safeParse(props);
  if (!parsed.success) {
    if (process.env.NODE_ENV !== 'production') {
      console.warn(`[telemetry] event ${name} failed validation:`, parsed.error.issues);
    }
    return null;
  }
  return parsed.data as EventPropsMap[E];
}

export type { EventPropsMap };
